import { useEffect, useRef, useState } from "react";

export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("sv-in"); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`sv-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>
  );
}

export function Counter({ to, suffix = "", duration = 1400 }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{v}{suffix}</span>;
}

const SOURCES_Y = [0.14, 0.32, 0.5, 0.68, 0.86];

export function NodeCanvas({ labels }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, W, H;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const particles = SOURCES_Y.flatMap((y, i) => [
      { src: i, t: (i * 0.19) % 1, speed: 0.0022 + i * 0.0004 },
      { src: i, t: (i * 0.19 + 0.5) % 1, speed: 0.0018 + i * 0.0005 },
    ]);
    let pulse = 0;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const sx = W * 0.06, rx = W * 0.62, ry = H * 0.5, rr = Math.min(W, H) * 0.085;
      const ex = W * 0.94;
      // curves
      SOURCES_Y.forEach((yy) => {
        const sy = H * yy;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.quadraticCurveTo(W * 0.38, sy + (ry - sy) * 0.25, rx - rr - 4, ry + (sy - ry) * 0.06);
        ctx.strokeStyle = "rgba(45,212,191,0.14)";
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      // particles
      particles.forEach((p) => {
        p.t += p.speed;
        if (p.t > 1) p.t = 0;
        const sy = H * SOURCES_Y[p.src];
        const cx = W * 0.38, cy = sy + (ry - sy) * 0.25;
        const tx = rx - rr - 4, ty = ry + (sy - ry) * 0.06;
        const t = p.t, mt = 1 - t;
        const x = mt * mt * sx + 2 * mt * t * cx + t * t * tx;
        const y = mt * mt * sy + 2 * mt * t * cy + t * t * ty;
        const a = 0.25 + 0.75 * t;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(94,234,212,${a})`;
        ctx.fill();
      });
      // source nodes
      SOURCES_Y.forEach((yy, i) => {
        ctx.beginPath();
        ctx.arc(sx, H * yy, 3.2, 0, Math.PI * 2);
        ctx.fillStyle = ["#5EEAD4", "#2DD4BF", "#14B8A6", "#0D9488", "#0F766E"][i];
        ctx.fill();
      });
      // calibration ring
      pulse += 0.02;
      const glow = 0.5 + 0.5 * Math.sin(pulse);
      ctx.beginPath();
      ctx.arc(rx, ry, rr + 7 + glow * 3, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(45,212,191,${0.08 + glow * 0.08})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.save();
      ctx.translate(rx, ry);
      ctx.rotate(pulse * 0.5);
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.setLineDash([10, 6]);
      ctx.strokeStyle = "rgba(45,212,191,0.85)";
      ctx.lineWidth = 1.8;
      ctx.stroke();
      ctx.restore();
      ctx.beginPath();
      ctx.arc(rx, ry, rr * 0.42, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(45,212,191,${0.25 + glow * 0.3})`;
      ctx.fill();
      // output beam
      ctx.beginPath();
      ctx.moveTo(rx + rr + 6, ry);
      ctx.lineTo(ex, ry);
      ctx.strokeStyle = "rgba(248,250,252,0.35)";
      ctx.lineWidth = 1.6;
      ctx.stroke();
      const bt = (pulse * 0.35) % 1;
      const bx = rx + rr + 6 + (ex - rx - rr - 6) * bt;
      ctx.beginPath();
      ctx.arc(bx, ry, 2.6, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(248,250,252,0.95)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(ex, ry, 3.4, 0, Math.PI * 2);
      ctx.fillStyle = "#F8FAFC";
      ctx.fill();
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <div className="relative w-full h-full" aria-label="Animated visual: evidence streams converging through the HCP Voice calibration ring into a single strategic signal" role="img">
      <canvas ref={canvasRef} className="absolute inset-0" />
      {labels && labels.map((l, i) => (
        <span key={l} className="absolute left-0 -translate-y-1/2 font-mono text-[9px] uppercase tracking-[0.14em] text-teal-200/70 bg-slate-950/60 border border-teal-500/20 rounded-full px-2 py-0.5 backdrop-blur-sm hidden sm:inline-block"
          style={{ top: `${SOURCES_Y[i] * 100}%`, marginLeft: "2%" }}>{l}</span>
      ))}
      <span className="absolute font-mono text-[9px] uppercase tracking-[0.14em] text-white/80 hidden sm:inline-block" style={{ left: "56%", top: "72%" }}>Calibration</span>
      <span className="absolute right-0 font-mono text-[9px] uppercase tracking-[0.14em] text-white bg-teal-500/20 border border-teal-400/40 rounded-full px-2 py-0.5" style={{ top: "38%" }}>Signal</span>
    </div>
  );
}
